/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { labelStyles } from '@zapier/style-encapsulation';
import { Animation, Icon, TooltipWrapper } from '@zapier/design-system';

type Props = {
  /*
   * `aria-label` for the `IconButton` if `label` isn't supplied.
   */
  ariaLabel?: string;
  /*
   * `aria-hidden` for the `IconButton`.
   */
  ariaHidden?: boolean;
  /*
   * Indicates whether or not `IconButton` is disabled.
   */
  disabled?: boolean;
  /**
   * `id` of the `input` that this component points to.
   */
  id?: string;
  /**
   * Indicates whether the user is currently searching.
   */
  isSearching: boolean;
  /*
   * Label for the `IconButton`.
   */
  label?: string;
};

const Styles = labelStyles('SearchTogglerOutOfApp', {
  root: css`
    width: 40px;
    height: 40px;
    border-radius: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--zds-gray-warm-6, #88827e);
    transition: ${Animation.transitionValue};

    &:hover,
    &:focus {
      background-color: transparent;
      color: var(--zds-gray-warm-10, #201515);
    }
  `,
});

/**
 * @private
 * Button that controls whether or not the search form is displayed.
 */
const SearchToggler = (props: Props) => {
  return (
    <TooltipWrapper
      content={props.label}
      position="south"
      shouldCorrectPosition={false}
      showTooltipOnTouchDevices={false}
    >
      {() => (
        <label
          aria-hidden={props.ariaHidden}
          aria-label={props.ariaLabel}
          css={Styles.root}
          htmlFor={props.id}
          data-zds
        >
          <Icon name={props.isSearching ? 'formX' : 'navSearch'} size={24} />
        </label>
      )}
    </TooltipWrapper>
  );
};

export default SearchToggler;
